import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import {
  BusinessLocationTssDto,
  UpdateTssDto,
  ChangeAdminPinDto,
  UpdateClientStateRequestStateEnum,
  ExportQueryDto,
} from "../../api-client";
import { taxationKeys } from "./taxation.keys";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 */

// ===========================
// TSS MUTATIONS
// ===========================

/**
 * Create a new TSS for a business location
 */
export const useCreateTss = () => {
  const { taxation } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: BusinessLocationTssDto) => taxation.createTss(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxationKeys.tssAll.all() });
      qc.invalidateQueries({ queryKey: taxationKeys.dashboard.all() });
    },
    meta: {
      successMessage: "TSS erfolgreich erstellt",
    },
  });
};

/**
 * Update TSS state (UNINITIALIZED → INITIALIZED)
 */
export const useUpdateTss = () => {
  const { taxation } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateTssDto) => taxation.updateTss(dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: taxationKeys.tssAll.all() });
      qc.invalidateQueries({
        queryKey: taxationKeys.tss.byId(variables.tssId),
      });
    },
    meta: {
      successMessage: "TSS-Status erfolgreich aktualisiert",
    },
  });
};

/**
 * Change admin PIN for a TSS
 */
export const useChangeAdminPin = () => {
  const { taxation } = useApi();

  return useMutation<any, Error, { tssId: string; dto: ChangeAdminPinDto }>({
    mutationFn: ({ tssId, dto }) => taxation.changeAdminPin(tssId, dto),
    meta: {
      successMessage: "Admin-PIN erfolgreich geändert",
    },
  });
};

/**
 * Authenticate admin for a TSS
 */
export const useAuthenticateAdmin = () => {
  const { taxation } = useApi();

  return useMutation<any, Error, string>({
    mutationFn: (tssId: string) => taxation.authenticateAdmin(tssId),
    meta: {
      successMessage: "Admin erfolgreich authentifiziert",
    },
  });
};

/**
 * Logout admin from a TSS
 */
export const useLogoutAdmin = () => {
  const { taxation } = useApi();

  return useMutation<any, Error, string>({
    mutationFn: (tssId: string) => taxation.logOutAdmin(tssId),
    meta: {
      successMessage: "Admin erfolgreich abgemeldet",
    },
  });
};

// ===========================
// CLIENT MUTATIONS
// ===========================

/**
 * Create a client for a TSS
 */
export const useCreateClient = () => {
  const { taxation } = useApi();
  const qc = useQueryClient();

  return useMutation<any, Error, string>({
    mutationFn: (tssId: string) => taxation.createClientTSS(tssId),
    onSuccess: (_data, tssId) => {
      qc.invalidateQueries({ queryKey: taxationKeys.clientAll.all() });
      qc.invalidateQueries({ queryKey: taxationKeys.dashboard.all() });
    },
    meta: {
      successMessage: "Client erfolgreich erstellt",
    },
  });
};

/**
 * Update client state
 */
export const useUpdateClient = () => {
  const { taxation } = useApi();
  const qc = useQueryClient();

  return useMutation<
    any,
    Error,
    { clientId: string; state: UpdateClientStateRequestStateEnum }
  >({
    mutationFn: ({ clientId, state }) =>
      taxation.updateClientState(clientId, { state }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxationKeys.clients.all() });
    },
    meta: {
      successMessage: "Client-Status erfolgreich aktualisiert",
    },
  });
};

// ===========================
// EXPORT MUTATIONS
// ===========================

/**
 * Trigger export for a TSS
 */
export const useTriggerExport = () => {
  const { taxation } = useApi();
  const qc = useQueryClient();

  return useMutation<any, Error, ExportQueryDto>({
    mutationFn: (dto) => taxation.exportTss(dto),
    onSuccess: (_data, variables) => {
      // Invalidate all exports and TSS-specific exports
      qc.invalidateQueries({ queryKey: taxationKeys.exports.all() });
      qc.invalidateQueries({
        queryKey: taxationKeys.exports.forTss(variables.tssId),
      });
      qc.invalidateQueries({ queryKey: taxationKeys.dashboard.all() });
    },
    meta: {
      successMessage: "Export erfolgreich ausgelöst",
    },
  });
};

/**
 * Download export file
 */
export const useDownloadExport = () => {
  const { taxation } = useApi();

  return useMutation<any, Error, { tssId: string; exportId: string }>({
    mutationFn: ({ tssId, exportId }) =>
      taxation.downloadExport(tssId, exportId, { responseType: "blob" }),
    meta: {
      successMessage: "Export erfolgreich heruntergeladen",
    },
  });
};
